import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Cooking from "../../assets/images/icons/cooking.png";
import CookCurrent from "../../assets/images/icons/cook-current.svg";
import CookComplete from "../../assets/images/icons/cook-complete.svg";
import Img from "../../assets/images/img.webp";
import Download from "../../assets/images/icons/download-icon.svg";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Row, Col, Modal, Button } from "react-bootstrap";
import {
  UI_CONSTANTS,
  getOrderStatusLabel,
  getDriverTypeLabel,
  getOrderTypeLabel,
  isPhoneBasedOrder,
} from "../../utils/constants";
import OrderInvoiceItems from "../../commons/OrderInvoiceItems/OrderInvoiceItems";
import {
  ADMIN_ORDER_STATUS_CHANGED,
  createAdminOrderSocket,
  subscribeAdminOrder,
  unsubscribeAdminOrder,
} from "../../utils/adminOrderSocket";
import {
  LIST_RETURN_KEYS,
  persistListReturn,
  consumeListReturn,
} from "../../utils/listNavigation";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

function ViewOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [refundModal, setRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [crDrModal, setCrDrModal] = useState(false);
  const [crDrSubmitting, setCrDrSubmitting] = useState(false);
  const [crDrForm, setCrDrForm] = useState({
    cr_dr_note_type: "debit",
    user_type: "Restaurant",
    restaurant_amount: "",
    driver_amount: "",
    description: "",
  });

  const resolveMediaUrl = (value) => {
    if (!value || typeof value !== "string") return null;
    if (value.startsWith("http")) return value;
    return `${process.env.REACT_APP_IMAGE_URL}/${value}`;
  };

  const getRecordingUrl = (order) =>
    resolveMediaUrl(
      order?.call_recording ??
        order?.recording ??
        order?.recording_url ??
        order?.call_recording_url ??
        order?.order_recording
    );

  const getTranscriptValue = (order) =>
    order?.transcript ?? order?.transcription ?? order?.call_transcript ?? order?.typescript ?? null;

  const hasTranscript = (order) => {
    const value = getTranscriptValue(order);
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  };

  const handleTranscriptDownload = () => {
    const value = getTranscriptValue(orderDetails);
    if (!value) {
      toast.error("Transcript not available");
      return;
    }

    if (typeof value === "string" && value.startsWith("http")) {
      window.open(value, "_blank", "noopener,noreferrer");
      return;
    }

    const content = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `order-${orderDetails?.order_number || id}-transcript.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentMethod = (method) => {
    if (method === "ONLINE") return "Online";
    if (method === "COD") return "Cash on Delivery";
    return method || "N/A";
  };

  const getRestaurantLogo = () => {
    const logo = orderDetails?.restaurant_detail?.logo;
    if (!logo) return Img;
    if (logo.startsWith("http")) return logo;
    return `${process.env.REACT_APP_IMAGE_URL}/${logo}`;
  };

  const timelineSteps = [
    { title: "Order Received", time: orderDetails?.createdAt, icon: CookComplete },
    { title: "Order Accepted", time: orderDetails?.order_accepted_at, icon: CookCurrent, cooking: true },
    { title: "Ready for Pickup", time: orderDetails?.order_ready_for_pickup_at },
    { title: "In Transit", time: orderDetails?.order_in_transit_at },
    { title: "Delivered", time: orderDetails?.order_completed_at },
  ];

  const getCurrentStepIndex = () => {
    if (!orderDetails) return -1;
    const fields = [
      "createdAt",
      "order_accepted_at",
      "order_ready_for_pickup_at",
      "order_in_transit_at",
      "order_completed_at",
    ];
    let index = -1;
    fields.forEach((field, i) => {
      if (orderDetails[field]) index = i;
    });
    return index;
  };

  const currentStep = getCurrentStepIndex();

  const recordingUrl = useMemo(
    () => (orderDetails ? getRecordingUrl(orderDetails) : null),
    [orderDetails]
  );
  const showPhoneOrderMedia = isPhoneBasedOrder(orderDetails?.order_type);
  const showTranscriptDownload = showPhoneOrderMedia && hasTranscript(orderDetails);

  const getOrderDetails = useCallback(async ({ silent = false } = {}) => {
    if (!id) return;

    if (!silent) {
      setLoading(true);
      setError(false);
    }
    try {
      const response = await handleApiCall("get", `${endpoints.orderDetails}/${id}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setOrderDetails(response?.data?.data);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching order details:", error);
      if (!silent) {
        setError(true);
      }
      // Network errors are already handled by handleApiCall
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    getOrderDetails();
  }, [getOrderDetails]);

  useEffect(() => {
    if (location.state?.from || location.state?.listState) {
      persistListReturn(
        LIST_RETURN_KEYS.ORDER.from,
        LIST_RETURN_KEYS.ORDER.state,
        location.state?.from,
        location.state?.listState
      );
    }
  }, [location.state]);

  useEffect(() => {
    if (!id) return undefined;

    const socket = createAdminOrderSocket();

    const handleConnect = () => {
      subscribeAdminOrder(socket, id);
    };

    const handleStatusChanged = (payload) => {
      console.log("[AdminOrderSocket] Event received ← admin_order_status_changed", payload);

      const payloadOrderId = payload?.order_id;
      if (payloadOrderId && String(payloadOrderId) !== String(id)) {
        return;
      }

      setOrderDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          order_status:
            payload?.order_status !== undefined ? payload.order_status : prev.order_status,
          delivery_status:
            payload?.delivery_status !== undefined
              ? payload.delivery_status
              : prev.delivery_status,
          order_status_name: payload?.order_status_name || prev.order_status_name,
          delivery_status_name: payload?.delivery_status_name || prev.delivery_status_name,
        };
      });

      getOrderDetails({ silent: true });
    };

    socket.on("connect", handleConnect);
    if (socket.connected) {
      subscribeAdminOrder(socket, id);
    }

    socket.on(ADMIN_ORDER_STATUS_CHANGED, handleStatusChanged);

    return () => {
      console.log("[AdminOrderSocket] Leaving order detail page — cleanup");
      unsubscribeAdminOrder(socket, id);
      socket.off("connect", handleConnect);
      socket.off(ADMIN_ORDER_STATUS_CHANGED, handleStatusChanged);
      socket.disconnect();
    };
  }, [id, getOrderDetails]);

  const handleBack = () => {
    const { from, listState } = consumeListReturn(
      LIST_RETURN_KEYS.ORDER.from,
      LIST_RETURN_KEYS.ORDER.state,
      location.state
    );

    if (from) {
      navigate(from, {
        state: {
          ...(listState ? { listState } : {}),
          ...(location.state?.customerListFrom
            ? { from: location.state.customerListFrom }
            : {}),
        },
      });
      return;
    }

    if (location.pathname.includes("/menu-and-restaurant/")) {
      navigate("/menu-and-restaurant-management");
      return;
    }

    navigate("/order-management");
  };

  const handleCloseRefundModal = () => {
    setRefundModal(false);
    setRefundReason("");
    setRefundAmount("");
  };

  const handleCloseCrDrModal = () => {
    setCrDrModal(false);
    setCrDrForm({
      cr_dr_note_type: "debit",
      user_type: "Restaurant",
      restaurant_amount: "",
      driver_amount: "",
      description: "",
    });
  };

  const handleCrDrFormChange = (field, value) => {
    setCrDrForm((prev) => ({ ...prev, [field]: value }));
  };

  const getOrderPrice = () => {
    const price = Number(
      orderDetails?.amount_to_pay ?? orderDetails?.total_amount ?? 0
    );
    return Number.isFinite(price) ? price : 0;
  };

  const getRefundStatusLabel = () => {
    const status =
      orderDetails?.refund_status_str ||
      orderDetails?.refund_status_name ||
      orderDetails?.refund_status ||
      orderDetails?.payment_status_str ||
      null;

    if (status === null || status === undefined || status === "") return "N/A";
    if (typeof status === "number") {
      if (status === 0) return "Not Refunded";
      if (status === 1) return "Partial Refund";
      if (status === 2) return "Refunded";
      return String(status);
    }
    return String(status);
  };

  const handleRefundSubmit = async () => {
    const orderId = orderDetails?._id || id;
    const orderPrice = getOrderPrice();
    const amount = Number(refundAmount);

    if (!orderId) {
      toast.error("Order ID not found");
      return;
    }
    if (!refundReason.trim()) {
      toast.error("Please enter refund reason");
      return;
    }
    if (!String(refundAmount).trim() || Number.isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid refund amount");
      return;
    }
    if (amount > orderPrice) {
      toast.error(`Refund amount cannot be larger than order price (£${orderPrice})`);
      return;
    }

    setRefundSubmitting(true);
    try {
      const response = await handleApiCall("post", endpoints.orderRefund, {
        order_id: orderId,
        refund_reason: refundReason.trim(),
        refund_amount: amount,
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(response?.data?.message || "Refund processed successfully");
        handleCloseRefundModal();
        getOrderDetails();
      }
    } catch (error) {
      console.error("Error processing refund:", error);
    } finally {
      setRefundSubmitting(false);
    }
  };

  const handleCrDrSubmit = async () => {
    const orderId = orderDetails?._id || id;
    if (!orderId) {
      toast.error("Order ID not found");
      return;
    }

    const { cr_dr_note_type, user_type, restaurant_amount, driver_amount, description } =
      crDrForm;
    const needsRestaurant = user_type === "Restaurant" || user_type === "Both";
    const needsDriver = user_type === "Driver" || user_type === "Both";
    const isBoth = user_type === "Both";
    const orderPrice = getOrderPrice();
    const restaurantAmt = needsRestaurant ? Number(restaurant_amount) : 0;
    const driverAmt = needsDriver ? Number(driver_amount) : 0;
    const totalAmt = restaurantAmt + driverAmt;

    if (!cr_dr_note_type) {
      toast.error("Please select note type");
      return;
    }
    if (!user_type) {
      toast.error("Please select user type");
      return;
    }
    if (needsRestaurant && !String(restaurant_amount).trim()) {
      toast.error("Please enter restaurant amount");
      return;
    }
    if (needsDriver && !String(driver_amount).trim()) {
      toast.error("Please enter driver amount");
      return;
    }
    if (needsRestaurant && (Number.isNaN(restaurantAmt) || restaurantAmt < 0)) {
      toast.error("Please enter a valid restaurant amount");
      return;
    }
    if (needsDriver && (Number.isNaN(driverAmt) || driverAmt < 0)) {
      toast.error("Please enter a valid driver amount");
      return;
    }
    if (isBoth) {
      if (totalAmt > orderPrice) {
        toast.error(
          `Total of restaurant and driver amounts (£${totalAmt.toFixed(
            2
          )}) cannot be larger than order price (£${orderPrice})`
        );
        return;
      }
    } else {
      if (needsRestaurant && restaurantAmt > orderPrice) {
        toast.error(
          `Restaurant amount cannot be larger than order price (£${orderPrice})`
        );
        return;
      }
      if (needsDriver && driverAmt > orderPrice) {
        toast.error(
          `Driver amount cannot be larger than order price (£${orderPrice})`
        );
        return;
      }
    }
    if (!description.trim()) {
      toast.error("Please enter description");
      return;
    }

    const payload = {
      cr_dr_note_type,
      user_type,
      restaurant_amount: needsRestaurant ? String(restaurant_amount).trim() : "0",
      driver_amount: needsDriver ? String(driver_amount).trim() : "0",
      order_id: orderId,
      description: description.trim(),
    };

    setCrDrSubmitting(true);
    try {
      const response = await handleApiCall("post", endpoints.addPayoutCrDrNote, payload);

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        toast.success(response?.data?.message || "CR/DR note added successfully");
        handleCloseCrDrModal();
        getOrderDetails();
      }
    } catch (error) {
      console.error("Error adding payout CR/DR note:", error);
    } finally {
      setCrDrSubmitting(false);
    }
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">View Order</h3>
            </div>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                </div>
              </div>
            ) : error || !orderDetails ? (
              <div className="text-center py-4">
                <p>{UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</p>
                <button className="btn btn-default rounded-lg" onClick={handleBack}>
                  Back
                </button>
              </div>
            ) : (
              <>
                <div>
                  <div className="mt-3 d-flex flex-wrap align-items-center">
                    <button className="btn btn-default rounded-lg" onClick={handleBack}>
                      Back
                    </button>
                    {Number(orderDetails?.order_status) ===
                      UI_CONSTANTS.ORDER_STATUS.COMPLETED && (
                      <div className="ml-auto d-flex flex-wrap">
                        <button
                          type="button"
                          className="btn btn-primary rounded-lg mr-2"
                          onClick={() => setCrDrModal(true)}
                        >
                          Add CR/DR Note
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger rounded-lg"
                          onClick={() => setRefundModal(true)}
                        >
                          Refund
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <Row className="mt-4">
                  <Col lg="3" className="pr-0">
                    <div className="imgCard" style={{ height: "200px", width: "100%" }}>
                      <img
                        src={getRestaurantLogo()}
                        alt={orderDetails?.restaurant_details?.restaurant_name || "Restaurant"}
                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                      />
                    </div>
                  </Col>
                  <Col lg="9">
                    <h3 className="border-bottom pb-2 mb-3">
                      {orderDetails?.restaurant_details?.restaurant_name || "N/A"}
                    </h3>
                    <div className="border rounded-14 overflow-hidden">
                      <table className="w-100 text-center mb-0">
                        <tbody>
                          <tr>
                            <td className="px-3 py-2 border-right border-bottom text-left">
                              <div className="font-weight-600">Order Status</div>
                              {getOrderStatusLabel(orderDetails?.order_status)}
                            </td>
                            <td className="px-3 py-2 border-right border-bottom">
                              <div className="font-weight-600">Date</div>
                              {formatDate(orderDetails?.createdAt)}
                            </td>
                            <td className="px-3 py-2 border-right border-bottom">
                              <div className="font-weight-600">Order ID</div>#{orderDetails?.order_number || "N/A"}
                            </td>
                            <td className="px-3 py-2 border-right border-bottom">
                              <div className="font-weight-600">Price</div>£{orderDetails?.amount_to_pay || "0"}
                            </td>
                            <td className="px-3 py-2 border-bottom">
                              <div className="font-weight-600">Driver Type</div>
                              {getDriverTypeLabel(orderDetails?.assigned_driver_type)}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 border-right text-left">
                              <div className="font-weight-600">Driver</div>
                              {orderDetails?.driver_details?.full_name ||
                                orderDetails?.assigned_driver_id?.full_name ||
                                "Not Assigned"}
                            </td>
                            <td className="px-3 py-2 border-right">
                              <div className="font-weight-600">Payment Method</div>
                              {getPaymentMethod(orderDetails?.payment_method)}
                            </td>
                            <td className="px-3 py-2 border-right">
                              <div className="font-weight-600">Delivery Time</div>
                              {formatDateTime(orderDetails?.order_completed_at)}
                            </td>
                            <td className="px-3 py-2 border-right">
                              <div className="font-weight-600">Order Type</div>
                              {getOrderTypeLabel(orderDetails?.order_type)}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-weight-600">Refund Status</div>
                              {getRefundStatusLabel()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
                <div className="border rounded-14 py-2 px-3 mt-3">
                  <h4 className="px-2 mb-2 font-weight-bold">{orderDetails?.customer_details?.full_name || "N/A"}</h4>
                  <div className="row mx-n2 align-items-stretch">
                    <div className="col-md-4 p-2 d-flex">
                      <div className="rounded-14 border p-3 w-100">
                        <strong>Mobile Number</strong>
                        <p className="mb-0 mt-2">{orderDetails?.customer_details?.phone || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-md-4 p-2 d-flex">
                      <div className="rounded-14 border p-3 w-100">
                        <strong>Email id</strong>
                        <p className="mb-0 mt-2">{orderDetails?.customer_details?.email || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-md-4 p-2 d-flex">
                      <div className="rounded-14 border p-3 w-100">
                        <strong>Address</strong>
                        <p className="mb-0 mt-2">{orderDetails?.delivery_address?.full_address || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="row mx-n2">
                    {showPhoneOrderMedia && (
                      <>
                        <div className="col-md-4 p-2">
                          <div className="rounded-14 border p-3">
                            <strong>Recording</strong>
                            <div className="mb-0 mt-2 d-flex">
                              {recordingUrl ? (
                                <audio controls style={{ width: "100%", height: "40px" }} src={recordingUrl} />
                              ) : (
                                <p className="mb-0 text-muted">N/A</p>
                              )}
                            </div>
                          </div>
                        </div>
                        {showTranscriptDownload && (
                          <div className="col-md-4 p-2">
                            <div className="rounded-14 border p-3 d-flex align-items-center">
                              <div className="flex-fill">
                                <strong>Transcript</strong>
                                <p className="mb-0 mt-2 small text-muted">Download call transcript</p>
                              </div>
                              <button
                                type="button"
                                className="btn btn-link p-0 border-0 bg-transparent"
                                onClick={handleTranscriptDownload}
                                aria-label="Download transcript"
                              >
                                <img src={Download} alt="Download transcript" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <div className={`col-md-4 p-2 ${!showPhoneOrderMedia ? "col-md-12" : ""}`}>
                      <div className="rounded-14 border p-3">
                        <strong>Customer Review</strong>
                        <p className="mb-0 mt-2">{orderDetails?.customer_review || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col md="6">
                    <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                      <div className="card-heading pb-3">
                        <h4 className="my-0 px-2">INVOICE DETAILS</h4>
                        <div className="mt-2 px-2">
                          <table className="table table-borderless mb-0">
                            <thead>
                              <tr>
                                <th className="px-3 pb-2 text-left">ITEM</th>
                                <th className="px-3 pb-2 text-center">QTY</th>
                                <th className="px-3 pb-2 text-end">PRICE</th>
                              </tr>
                            </thead>
                            <tbody className="font-weight-600">
                              <OrderInvoiceItems
                                orderDishes={orderDetails?.order_dishes}
                                itemCellClass="text-left px-3 pb-1"
                                qtyCellClass="text-center px-3 pb-1"
                                priceCellClass="text-end px-3 pb-1"
                                emptyCellClass="text-center px-3 pb-1"
                              />
                              <tr>
                                <td colSpan="3" style={{ height: "20px" }}></td>
                              </tr>
                              <tr>
                                <td className="text-left px-3">Service Fee</td>
                                <td className="px-3"></td>
                                <td className="text-end px-3">£{orderDetails?.service_fee || "0"}</td>
                              </tr>
                              <tr>
                                <td className="text-left px-3">Delivery Charge</td>
                                <td className="px-3"></td>
                                <td className="text-end px-3">£{orderDetails?.delivery_charge || "0"}</td>
                              </tr>
                            </tbody>
                            <tfoot>
                              <tr className="font-weight-600">
                                <td colSpan="2" className="text-left border-top px-3 pb-1 pt-2">
                                  TOTAL
                                </td>
                                <td className="text-end border-top px-3 pb-1 pt-2">£{orderDetails?.amount_to_pay || "0"}</td>
                              </tr>
                              <tr>
                                <td colSpan="3" className="text-left px-3 pb-1">
                                  <span className="font-weight-600">Instruction to Restaurant</span>: {orderDetails?.restaurant_instruction || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td colSpan="3" className="text-left px-3 pt-1">
                                  <span className="font-weight-600">Driver Instructions</span>: {orderDetails?.driver_instruction || "N/A"}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                      <div className="timeline">
                        {timelineSteps.map((step, index) => {
                          const isCompleted = index < currentStep;
                          const isCurrent = index === currentStep;

                          return (
                            <div
                              key={index}
                              className={`timeline-item ${isCompleted ? "completed" : isCurrent ? "current" : ""}`}
                            >
                              <div className={`status-circle ${isCurrent ? "active" : ""}`}>
                                {isCompleted ? (
                                  <img src={CookComplete} alt="complete" />
                                ) : isCurrent ? (
                                  <img src={CookCurrent} alt="current" />
                                ) : (
                                  <div className="pending-circle"></div>
                                )}
                              </div>
                              <div className="timeline-content">
                                <h5 className="d-flex align-items-center">
                                  {step.title}
                                  {step.cooking && isCurrent && (
                                    <>
                                      <small className="mx-2">&#9679;</small>
                                      <span className="text-danger d-flex align-items-center">
                                        Cooking
                                        <img src={Cooking} alt="Cooking" className="ml-2 mt-n1" />
                                      </span>
                                    </>
                                  )}
                                </h5>
                                <small>{formatDateTime(step.time)}</small>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </div>
      </div>
      <Modal show={refundModal} onHide={handleCloseRefundModal} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Refund</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRefundSubmit();
            }}
          >
            <div className="form-group mb-3">
              <label>Refund Amount (Max £{getOrderPrice()})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                max={getOrderPrice()}
                className="form-control"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                disabled={refundSubmitting}
                required
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="form-group mb-3">
              <label>Refund Reason</label>
              <textarea
                className="form-control"
                rows="4"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                disabled={refundSubmitting}
                required
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCloseRefundModal}
                className="mr-2"
                disabled={refundSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={refundSubmitting}>
                {refundSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={crDrModal} onHide={handleCloseCrDrModal} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Add CR/DR Note</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCrDrSubmit();
            }}
          >
            <div className="form-group mb-3">
              <label>Note Type</label>
              <select
                className="form-control"
                value={crDrForm.cr_dr_note_type}
                onChange={(e) =>
                  handleCrDrFormChange("cr_dr_note_type", e.target.value)
                }
                disabled={crDrSubmitting}
                style={{ colorScheme: "dark" }}
              >
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
            </div>
            <div className="form-group mb-3">
              <label>User Type</label>
              <select
                className="form-control"
                value={crDrForm.user_type}
                onChange={(e) => handleCrDrFormChange("user_type", e.target.value)}
                disabled={crDrSubmitting}
                style={{ colorScheme: "dark" }}
              >
                <option value="Restaurant">Restaurant</option>
                <option value="Driver">Driver</option>
                <option value="Both">Both</option>
              </select>
            </div>
            {(crDrForm.user_type === "Restaurant" ||
              crDrForm.user_type === "Both") && (
              <div className="form-group mb-3">
                <label>
                  Restaurant Amount
                  {crDrForm.user_type === "Both"
                    ? ` (Remaining £${Math.max(
                        0,
                        getOrderPrice() - (Number(crDrForm.driver_amount) || 0)
                      ).toFixed(2)})`
                    : ` (Max £${getOrderPrice()})`}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={
                    crDrForm.user_type === "Both"
                      ? Math.max(
                          0,
                          getOrderPrice() - (Number(crDrForm.driver_amount) || 0)
                        )
                      : getOrderPrice()
                  }
                  className="form-control"
                  value={crDrForm.restaurant_amount}
                  onChange={(e) =>
                    handleCrDrFormChange("restaurant_amount", e.target.value)
                  }
                  disabled={crDrSubmitting}
                  style={{ colorScheme: "dark" }}
                />
              </div>
            )}
            {(crDrForm.user_type === "Driver" || crDrForm.user_type === "Both") && (
              <div className="form-group mb-3">
                <label>
                  Driver Amount
                  {crDrForm.user_type === "Both"
                    ? ` (Remaining £${Math.max(
                        0,
                        getOrderPrice() - (Number(crDrForm.restaurant_amount) || 0)
                      ).toFixed(2)})`
                    : ` (Max £${getOrderPrice()})`}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={
                    crDrForm.user_type === "Both"
                      ? Math.max(
                          0,
                          getOrderPrice() - (Number(crDrForm.restaurant_amount) || 0)
                        )
                      : getOrderPrice()
                  }
                  className="form-control"
                  value={crDrForm.driver_amount}
                  onChange={(e) =>
                    handleCrDrFormChange("driver_amount", e.target.value)
                  }
                  disabled={crDrSubmitting}
                  style={{ colorScheme: "dark" }}
                />
              </div>
            )}
            {crDrForm.user_type === "Both" && (
              <p className="text-muted small mb-3">
                Total: £
                {(
                  (Number(crDrForm.restaurant_amount) || 0) +
                  (Number(crDrForm.driver_amount) || 0)
                ).toFixed(2)}{" "}
                / £{getOrderPrice()}
              </p>
            )}
            <div className="form-group mb-3">
              <label>Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={crDrForm.description}
                onChange={(e) =>
                  handleCrDrFormChange("description", e.target.value)
                }
                disabled={crDrSubmitting}
                required
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCloseCrDrModal}
                className="mr-2"
                disabled={crDrSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={crDrSubmitting}>
                {crDrSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ViewOrder;
